/*
 * Partes admin — vista supervisor. Solo admin.
 * Lista horas trabajadas por operario / día en un rango, con firma y sites visitados.
 * Permite descargar CSV.
 */
import React, { useEffect, useState, useCallback } from 'react';
import api from '../api/client';
import { Loader2, Download, ShieldCheck, Clock } from 'lucide-react';
import { useSyncListener } from '../hooks/useDataSync';

const fmtMin = (m) => {
  if (!Number.isFinite(m) || m <= 0) return '0h 00m';
  return `${Math.floor(m/60)}h ${String(m%60).padStart(2,'0')}m`;
};
const isoDate = (d) => d.toISOString().slice(0, 10);

const PartesAdmin = () => {
  const today = new Date();
  const twoWeeksAgo = new Date(today); twoWeeksAgo.setDate(twoWeeksAgo.getDate() - 13);
  const [since, setSince] = useState(isoDate(twoWeeksAgo));
  const [until, setUntil] = useState(isoDate(today));
  const [userId, setUserId] = useState('');
  const [users, setUsers] = useState([]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [rep, us] = await Promise.all([
        api.get('/time-entries/reports', { params: { since, until, user_id: userId || undefined } }),
        api.get('/admin/users').catch(() => ({ data: [] })),
      ]);
      setData(rep.data);
      setUsers(us.data);
    } finally { setLoading(false); }
  }, [since, until, userId]);
  useEffect(() => { load(); }, [load]);
  useSyncListener(load, ['time_entries', 'workday_signatures']);

  const exportCsv = () => {
    if (!data?.rows) return;
    const header = ['Operario', 'Email', 'Día', 'Horas trabajadas', 'Pausas', 'Movimientos', 'Sites visitados', 'Firmado'];
    const lines = [header.join(';')];
    for (const r of data.rows) {
      lines.push([
        r.user_name, r.user_email, r.date, fmtMin(r.total_minutes), fmtMin(r.break_minutes),
        r.entries_count, (r.sites_visitados || []).join(' / '), r.signed ? 'SÍ' : 'NO',
      ].map((v) => `"${(v ?? '').toString().replace(/"/g, '""')}"`).join(';'));
    }
    const blob = new Blob(["\uFEFF" + lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `partes_${since}_${until}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="p-4 sm:p-8 max-w-6xl mx-auto space-y-6" data-testid="partes-admin-page">
      <div>
        <div className="text-xs font-bold tracking-widest text-[#A3FF12]">SUPERVISOR</div>
        <h1 className="text-3xl sm:text-4xl font-black mt-1">Partes horarios</h1>
        <p className="text-white/50 text-sm mt-1">Horas trabajadas por operario y día, con firma y sites visitados.</p>
      </div>

      <div className="flex flex-wrap gap-3 items-end bg-black/40 border border-white/10 rounded-2xl p-4">
        <div>
          <label className="text-white/50 text-xs block mb-1">Desde</label>
          <input type="date" value={since} onChange={(e) => setSince(e.target.value)} data-testid="filter-since"
            className="bg-black border border-white/10 rounded-lg px-3 py-2 text-white text-sm"/>
        </div>
        <div>
          <label className="text-white/50 text-xs block mb-1">Hasta</label>
          <input type="date" value={until} onChange={(e) => setUntil(e.target.value)} data-testid="filter-until"
            className="bg-black border border-white/10 rounded-lg px-3 py-2 text-white text-sm"/>
        </div>
        <div>
          <label className="text-white/50 text-xs block mb-1">Operario</label>
          <select value={userId} onChange={(e) => setUserId(e.target.value)} data-testid="filter-user"
            className="bg-black border border-white/10 rounded-lg px-3 py-2 text-white text-sm min-w-[200px]">
            <option value="">Todos</option>
            {users.map((u) => <option key={u.id} value={u.id}>{u.name} ({u.email})</option>)}
          </select>
        </div>
        <div className="flex-1"/>
        <button onClick={exportCsv} disabled={!data?.rows?.length} data-testid="btn-export-csv"
          className="flex items-center gap-2 bg-white/5 border border-white/10 hover:bg-white/10 px-4 py-2 rounded-lg text-sm text-white disabled:opacity-30">
          <Download size={16}/> Exportar CSV
        </button>
      </div>

      {loading ? (
        <div className="p-12 flex items-center justify-center"><Loader2 className="animate-spin text-[#A3FF12]" size={32}/></div>
      ) : (!data?.rows || data.rows.length === 0) ? (
        <div className="p-12 text-center text-white/40 text-sm bg-black/40 border border-white/10 rounded-2xl">
          No hay fichajes en este rango.
        </div>
      ) : (
        <div className="bg-black/40 border border-white/10 rounded-2xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="partes-table">
              <thead className="bg-white/5 text-white/60 text-xs uppercase tracking-wide">
                <tr>
                  <th className="text-left px-4 py-3">Operario</th>
                  <th className="text-left px-4 py-3">Día</th>
                  <th className="text-right px-4 py-3">Trabajadas</th>
                  <th className="text-right px-4 py-3">Pausa</th>
                  <th className="text-right px-4 py-3">Mov.</th>
                  <th className="text-left px-4 py-3">Sites visitados</th>
                  <th className="text-center px-4 py-3">Firma</th>
                </tr>
              </thead>
              <tbody>
                {data.rows.map((r) => (
                  <tr key={`${r.user_id}-${r.date}`} className="border-t border-white/5 hover:bg-white/[0.02]">
                    <td className="px-4 py-3">
                      <div className="text-white font-bold">{r.user_name || '—'}</div>
                      <div className="text-white/40 text-xs">{r.user_email}</div>
                    </td>
                    <td className="px-4 py-3 text-white/80">{r.date}</td>
                    <td className="px-4 py-3 text-right font-black text-[#A3FF12]">{fmtMin(r.total_minutes)}</td>
                    <td className="px-4 py-3 text-right text-orange-300">{fmtMin(r.break_minutes)}</td>
                    <td className="px-4 py-3 text-right text-white/60">{r.entries_count}</td>
                    <td className="px-4 py-3 text-blue-400 text-xs max-w-[240px]">
                      {(r.sites_visitados || []).length === 0 ? <span className="text-white/30">—</span> :
                        <div className="flex flex-wrap gap-1">
                          {r.sites_visitados.slice(0, 5).map((s) => (
                            <span key={s} className="bg-blue-500/10 border border-blue-500/20 rounded-full px-2 py-0.5">{s}</span>
                          ))}
                          {r.sites_visitados.length > 5 && <span className="text-white/40">+{r.sites_visitados.length - 5}</span>}
                        </div>
                      }
                    </td>
                    <td className="px-4 py-3 text-center">
                      {r.signed
                        ? <ShieldCheck className="text-[#A3FF12] inline" size={18}/>
                        : <Clock className="text-white/30 inline" size={18}/>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default PartesAdmin;
